import React, { useCallback, useEffect, useState } from 'react';
import { CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout, StudentLayout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../api/examApi';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATION_TYPES, getNotificationMeta, getNotificationTarget } from '../lib/notificationTypes';

const PAGE_SIZE = 20;

function formatTimestamp(iso) {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function NotificationsContent() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback((pageToLoad, filter) => {
    setLoading(true);
    notificationApi.list(pageToLoad, PAGE_SIZE, filter || undefined)
      .then(({ data }) => {
        setNotifications(pageToLoad === 1 ? data : (prev) => [...prev, ...data]);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
    load(1, typeFilter);
  }, [typeFilter, load]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next, typeFilter);
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationApi.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  return (
    <div className="animate-fade-in max-w-3xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Notifications</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Your full notification history</p>
        </div>
        <button
          onClick={handleMarkAllRead}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          data-testid="mark-all-read-btn"
        >
          <CheckCheck size={16} />
          Mark all read
        </button>
      </div>

      <div className="flex items-center gap-2">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
          data-testid="notification-type-filter"
        >
          <option value="">All Types</option>
          {NOTIFICATION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
        {loading && notifications.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No notifications yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {notifications.map((n) => {
              const meta = getNotificationMeta(n.type);
              const Icon = meta.icon;
              return (
                <li
                  key={n.id}
                  onClick={() => {
                    if (!n.is_read) handleMarkRead(n.id);
                    const to = getNotificationTarget(n);
                    if (to) navigate(to);
                  }}
                  className={`flex gap-3 px-5 py-4 cursor-pointer transition-colors ${
                    n.is_read ? 'bg-white dark:bg-slate-800' : 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10'
                  }`}
                  data-testid={`notification-row-${n.id}`}
                >
                  <Icon size={18} className={`mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{meta.label}</span>
                      {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                    </div>
                    <p className="text-sm text-slate-800 dark:text-slate-100 mt-0.5">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatTimestamp(n.created_at)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
            data-testid="load-more-btn"
          >
            {loading ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const { user } = useAuth();

  if (user?.user_type === 'student') {
    return (
      <StudentLayout title="Notifications">
        <NotificationsContent />
      </StudentLayout>
    );
  }

  return (
    <DashboardLayout title="Notifications">
      <NotificationsContent />
    </DashboardLayout>
  );
}
